import { createTheme, type ThemeOptions } from '@mui/material/styles';
import Grow from '@mui/material/Grow';
import { actionTone, ink, radius } from '@/theme/tokens';
// Registers the MuiDataGrid key on the theme's `components` (types only).
import type {} from '@mui/x-data-grid/themeAugmentation';

/**
 * MUI theme tuned to MATCH the legacy `assets/css/payroll-theme.css` exactly.
 * Variable mapping (legacy → MUI):
 *   --bg #f4f2fb               → background.default        (page bg, soft lavender-grey)
 *   --surface #ffffff          → background.paper          (card bg)
 *   --surface2 #f7f9fc         → grey.50                   (alt surface)
 *   --s2 #f3f7fb               → grey.100                  (panel/header bg)
 *   --s3 #e8edf2               → grey.200
 *   --border #e1e5e8           → divider
 *   --ink #333333              → text.primary
 *   --ink2 #666666             → text.secondary
 *   --ink3 #999999             → text.disabled
 *   --accent #108ae6           → primary.main              (the trademark blue)
 *   --acc2 #e8f4fd             → primary.light             (accent fill)
 *   --green #1A7845            → success.main
 *   --red #C22C2C              → error.main
 *   --amber #8C5100            → warning.main
 *   --r 8px / --rl 12px        → shape.borderRadius (8)
 *   font Lato                  → typography.fontFamily
 *
 * The blue sidebar (#108ae6 with white nav items) is rendered via direct
 * styles in <Sidebar> rather than the theme; same for the topbar.
 */
/** The options behind `theme` — BrandedThemeProvider rebuilds from them. */
/** Tinted row-action box (wireframe .btn-icon-sm): cyan edit by default,
 *  blue for primary/info, red for error, green for success, amber for warning. */
const tint = (t: { bg: string; fg: string }) => ({
  color: t.fg, borderColor: t.fg, backgroundColor: t.bg,
  '&:hover': { backgroundColor: t.fg, borderColor: t.fg, color: '#fff' },
});
/** A form field carrying a hint (not an error) under its box. */
const HINT_FIELD = '.MuiFormControl-root:has(> .MuiFormHelperText-root:not(.Mui-error):not(:empty)):not(:has(> .MuiFormHelperText-root > .notranslate))';
const HINT_BUBBLE = {
  backgroundColor: '#2f2f2f', color: '#fff', fontSize: 12.5, fontWeight: 400, lineHeight: 1.6,
  padding: '6px 12px', borderRadius: 4, textAlign: 'center' as const,
};
/** A row of 2+ StatCards, direct or one per Grid item. */
const STAT_ROW = ':is(*:has(> .ukv-stat + .ukv-stat), *:has(> :has(> .ukv-stat:only-child) + :has(> .ukv-stat:only-child)))';
export const ROW_ACTIONS = {
  width: 28, height: 28, borderRadius: radius.actionIcon, border: '1px solid',
  transition: 'background-color 140ms, border-color 140ms, color 140ms',
  '& .MuiSvgIcon-root': { fontSize: 15 },
  ...tint(actionTone.edit),
  '&.MuiIconButton-colorPrimary, &.MuiIconButton-colorInfo': tint(actionTone.view),
  '&.MuiIconButton-colorError': tint(actionTone.del),
  '&.MuiIconButton-colorSuccess': tint(actionTone.add),
  '&.MuiIconButton-colorWarning': tint(actionTone.warn),
  // color="inherit": a plain grey box, for "more" menus and other neutral actions.
  '&.MuiIconButton-colorInherit': {
    color: '#6b7683', borderColor: '#d5dbe1', backgroundColor: '#fff',
    '&:hover': { backgroundColor: '#f3f5f7', borderColor: '#c7ced6', color: '#333' },
  },
  // The icon decides the tone, so every page looks the same whatever color it passed.
  '&:has(> [data-testid^="Delete"])': tint(actionTone.del),
  '&:has(> [data-testid^="Visibility"], > [data-testid^="RemoveRedEye"])': tint(actionTone.view),
  '&:has(> [data-testid^="Edit"])': tint(actionTone.edit),
  '&.Mui-disabled': { opacity: 0.4 },
};

export const themeOptions: ThemeOptions = {
  palette: {
    primary: {
      main:        '#108ae6',
      light:       '#e8f4fd',
      dark:        '#0d79c7',
      contrastText: '#ffffff',
    },
    secondary: { main: '#666666' },
    success:   { main: '#1bcb84', light: '#e9fbf3' },
    error:     { main: '#dc5640', light: '#faece9' },
    warning:   { main: '#f1b44c', light: '#fef9ed' },
    info:      { main: '#0d7cc7', light: '#e8f4fd' },
    background: {
      default: '#f3f3f9',   // page bg — wireframe --bg
      paper:   '#ffffff',
    },
    text: {
      primary:   '#333333',
      secondary: '#666666',
      disabled:  '#999999',
    },
    divider: '#e1e5e8',
    grey: {
      50:  '#f7f9fc',
      100: '#f3f7fb',
      200: '#e8edf2',
      300: '#d7dfe7',
      400: '#c7ced6',
      500: '#999999',
      600: '#8a8378',
      700: '#666666',
      800: '#4a4742',
      900: '#333333',
    },
  },
  typography: {
    fontFamily: '"Lato", "Plus Jakarta Sans", system-ui, -apple-system, "Segoe UI", Roboto, sans-serif',
    fontSize: 14,
    h1: { fontSize: 24, fontWeight: 600, letterSpacing: '-0.01em', color: '#333333' },
    h2: { fontSize: 20, fontWeight: 700, color: '#333333' },
    h3: { fontSize: 16, fontWeight: 700, color: '#333333' },
    button: { textTransform: 'none', fontWeight: 600, letterSpacing: 0 },
    body1: { fontSize: 14 },
    body2: { fontSize: 13.5 },
    caption: { fontSize: 12.5 },
  },
  shape: { borderRadius: 8 },
  shadows: [
    'none',
    '0 1px 3px rgba(24, 28, 44, 0.07)',
    '0 1px 3px rgba(24, 28, 44, 0.07), 0 4px 14px rgba(24, 28, 44, 0.04)',
    ...Array(22).fill('0 4px 18px rgba(24, 28, 44, 0.06)'),
  ] as never,
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        // Keep html, body and #root at exact viewport dimensions and never
        // grow — the AppShell handles all internal scrolling. This prevents
        // any double-scrollbar / horizontal-overflow surprises.
        html:  { height: '100%', overflow: 'hidden' },
        body:  { height: '100%', margin: 0, backgroundColor: '#f3f3f9', overflow: 'hidden' },
        '#root': { height: '100%' },
        // Field hints live in a dark tooltip over the label (shown on hover / focus), not as
        // grey text under the box. Errors stay visible below.
        [HINT_FIELD]: { position: 'relative' },
        [`${HINT_FIELD} > .MuiFormHelperText-root`]: {
          ...HINT_BUBBLE, position: 'absolute', left: 0, bottom: 'calc(100% + 8px)', margin: 0,
          width: 'max-content', maxWidth: 300, zIndex: 1600, pointerEvents: 'none',
          opacity: 0, visibility: 'hidden', transition: 'opacity 140ms, visibility 140ms',
          '&::after': {
            content: '""', position: 'absolute', top: '100%', left: 18,
            border: '5px solid transparent', borderTopColor: HINT_BUBBLE.backgroundColor,
          },
        },
        [`${HINT_FIELD}:is(:hover, :focus-within) > .MuiFormHelperText-root`]: { opacity: 1, visibility: 'visible' },
        // In a popup the hint opens below the field, so the first row's bubble is never cut off at the top.
        [`.MuiDialog-root ${HINT_FIELD} > .MuiFormHelperText-root`]: {
          bottom: 'auto', top: 'calc(100% + 8px)',
          '&::after': { top: 'auto', bottom: '100%', borderTopColor: 'transparent', borderBottomColor: HINT_BUBBLE.backgroundColor },
        },
        // A table's card uses the table radius and clips, so the grey header row can't poke past its corner.
        '.MuiPaper-root:has(> div > .MuiDataGrid-root), .MuiPaper-root:has(> .MuiDataGrid-root)': {
          borderRadius: radius.table, overflow: 'hidden',
        },
        // Every tooltip in the wireframe's dark style, with a pointer.
        '.MuiTooltip-popper .MuiTooltip-tooltip': { ...HINT_BUBBLE, position: 'relative' },
        '.MuiTooltip-popper .MuiTooltip-arrow': { color: HINT_BUBBLE.backgroundColor },
        '.MuiTooltip-popper .MuiTooltip-tooltip:not(.MuiTooltip-tooltipArrow)::after': {
          content: '""', position: 'absolute', border: '5px solid transparent',
        },
        '.MuiTooltip-popper[data-popper-placement^="top"] .MuiTooltip-tooltip:not(.MuiTooltip-tooltipArrow)::after': {
          top: '100%', left: 'calc(50% - 5px)', borderTopColor: HINT_BUBBLE.backgroundColor,
        },
        '.MuiTooltip-popper[data-popper-placement^="bottom"] .MuiTooltip-tooltip:not(.MuiTooltip-tooltipArrow)::after': {
          bottom: '100%', left: 'calc(50% - 5px)', borderBottomColor: HINT_BUBBLE.backgroundColor,
        },
        '.MuiTooltip-popper[data-popper-placement^="left"] .MuiTooltip-tooltip:not(.MuiTooltip-tooltipArrow)::after': {
          left: '100%', top: 'calc(50% - 5px)', borderLeftColor: HINT_BUBBLE.backgroundColor,
        },
        '.MuiTooltip-popper[data-popper-placement^="right"] .MuiTooltip-tooltip:not(.MuiTooltip-tooltipArrow)::after': {
          right: '100%', top: 'calc(50% - 5px)', borderRightColor: HINT_BUBBLE.backgroundColor,
        },
        // A row of <StatCard>s (direct, or one per Grid item) joins into one
        // wireframe summary strip, so no page can show loose stat boxes.
        [STAT_ROW]: {
          display: 'inline-flex !important', flexWrap: 'wrap', gap: '0 !important', width: 'fit-content !important', alignSelf: 'flex-start',
          maxWidth: '100%', margin: '0 0 16px 0 !important', border: `1px solid ${ink.border}`,
          borderRadius: radius.stat, backgroundColor: '#fff', overflow: 'hidden',
        },
        [`${STAT_ROW} > *`]: {
          margin: '0 0 0 -1px !important', flex: '0 0 auto !important', maxWidth: 'none !important', width: 'auto !important',
        },
        [`${STAT_ROW} > *:not(.ukv-stat)`]: { padding: '0 !important' },
        [`${STAT_ROW} .ukv-stat`]: { border: '0 !important', borderLeft: `1px solid ${ink.border} !important`, borderRadius: '0 !important' },
      },
    },
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        // .section-card / .table-wrap — 12px, not the global 8px
        root: {
          backgroundImage: 'none', borderRadius: 12,
          // A card that only wraps a table takes the table radius.
          '&:has(> .MuiTable-root), &:has(> .MuiTableContainer-root), &.MuiTableContainer-root':
            { borderRadius: radius.table, overflow: 'hidden' },
          // Wireframe tabs are a plain underline row, never a boxed card.
          '&:has(> .MuiTabs-root:first-child)': { border: 0, borderRadius: 0, background: 'transparent', boxShadow: 'none' },
        },
        outlined: { borderColor: ink.border },
      },
    },
    MuiCard: {
      defaultProps: { elevation: 0 },
      styleOverrides: { root: { borderRadius: 12, border: '1px solid #e1e5e8' } },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        // .btn — 34px tall, 8px radius, 14px/500 label
        root: {
          borderRadius: 8,
          minHeight: 34,
          padding: '0 16px',
          fontSize: 14,
          fontWeight: 500,
          // Tight line box so the label sits dead-centre vertically (Lato's
          // cap-height otherwise rides high in the default 1.75 line box).
          lineHeight: 1.2,
          // Every "+ Add / New" button is the wireframe green add button, whatever variant a page passed.
          '&:is(.MuiButton-contained, .MuiButton-outlined):has(> .MuiButton-startIcon > [data-testid="AddIcon"])': {
            backgroundColor: '#edfaf3', color: '#1bcb84', border: '1px solid #1bcb84', boxShadow: 'none',
            whiteSpace: 'nowrap', flexShrink: 0,
            '&:hover': { backgroundColor: '#e3f7ec', color: '#12a86a', borderColor: '#12a86a' },
            '&.Mui-disabled': { backgroundColor: '#f4f6f8', color: '#a9b3bd', borderColor: '#dfe4e8' },
          },
        },
        // .btn-sm
        sizeSmall: { minHeight: 30, padding: '0 12px', fontSize: 13, borderRadius: 6 },
        // Wireframe outlined buttons draw their border in the label colour.
        // Neutral (color="inherit") ones keep a light grey border.
        outlined: {
          borderColor: 'currentColor', '&:hover': { borderColor: 'currentColor' },
          '&.MuiButton-colorInherit, &.MuiButton-colorInherit:hover': { borderColor: '#d5dbe1' },
        },
        // A disabled primary button stays blue, just faded (wireframe "Save (0)").
        containedPrimary: { '&.Mui-disabled': { backgroundColor: 'rgba(16,138,230,.4)', color: '#fff' } },
        // A deeper, professional green for a contained success button (Approve)
        // instead of the neon mint of success.main, with a clean darker hover.
        containedSuccess: {
          backgroundColor: '#1aa865', color: '#fff',
          '&:hover': { backgroundColor: '#158a53', color: '#fff' },
        },
        // Amber action (Run for skipped): white label, deeper amber on hover, never a black label.
        containedWarning: {
          backgroundColor: '#e39a2b', color: '#fff',
          '&:hover': { backgroundColor: '#c98314', color: '#fff' },
        },
        // Tighten the icon↔label gap (MUI's default startIcon margin is too wide).
        startIcon: { marginRight: 6, marginLeft: -2, '& > *:nth-of-type(1)': { fontSize: 17 } },
        endIcon: { marginLeft: 6, marginRight: -2, '& > *:nth-of-type(1)': { fontSize: 17 } },
      },
    },
    MuiDialog: {
      // A gentle scale+fade on open (MUI Grow) instead of a plain fade, so a
      // dialog reads as "popped open" rather than just appearing. One place,
      // every dialog in the app.
      defaultProps: { transitionDuration: 220, TransitionComponent: Grow },
      styleOverrides: { paper: { borderRadius: radius.dialog } },
    },
    MuiDialogContent: {
      styleOverrides: { root: { padding: '20px 24px' }, dividers: { padding: '20px 24px', borderColor: ink.border } },
    },
    MuiDialogActions: {
      styleOverrides: { root: { padding: '12px 24px', gap: 8, '& > :not(style) ~ :not(style)': { marginLeft: 0 } } },
    },
    MuiMenu: { styleOverrides: { paper: { borderRadius: radius.menu } } },
    MuiPopover: { styleOverrides: { paper: { borderRadius: radius.menu } } },
    MuiAutocomplete: { styleOverrides: { paper: { borderRadius: radius.menu } } },
    MuiDialogTitle: {
      styleOverrides: {
        // position:relative so the top-right close (X) button anchors here;
        // pad the right so the title text never runs under it.
        root: { position: 'relative', padding: '20px 52px 16px 24px', fontSize: 18, fontWeight: 600, lineHeight: 1.3 },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: { minHeight: 40, borderBottom: `1px solid ${ink.border}` },
        indicator: { height: 3, borderRadius: '3px 3px 0 0', backgroundColor: '#108ae6' },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none', fontWeight: 600, fontSize: 14, minHeight: 40,
          '&.Mui-selected': { color: '#108ae6' },
        },
      },
    },
    MuiTextField: {
      // Always float the label (notched outline) so every field reads the same
      // whether it's empty or filled — otherwise filled fields float the label
      // while empty ones centre it, and a form looks uneven row to row. Also
      // sidesteps the label-overlap glitch when values arrive asynchronously.
      defaultProps: {
        size: 'small', variant: 'outlined', fullWidth: true,
        InputLabelProps: { shrink: true },
        // An empty select shows its "" option ("All", "Any") instead of a blank box.
        SelectProps: { displayEmpty: true },
      },
    },
    MuiSelect: {
      defaultProps: { size: 'small', displayEmpty: true },
      styleOverrides: {
        // With no "" option to show, an empty select reads "Select" in grey.
        select: { '& > .notranslate::after': { content: '"Select"', color: '#9aa3ad' } },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        outlined: {
          position: 'relative', transform: 'none', maxWidth: '100%', marginBottom: 6,
          fontSize: 13.5, fontWeight: 600, lineHeight: 1.3, color: '#333',
          '&.MuiInputLabel-shrink': { transform: 'none' },
          '&.Mui-focused': { color: '#333' },
          '&.Mui-error': { color: '#dc5640' },
          '&.Mui-disabled': { color: '#999' },
        },
        asterisk: { color: '#666' },
      },
    },
    MuiStepButton: {
      // No hover background box — on an alternativeLabel stepper it balloons
      // past the step and over the card edge. The cursor already signals the
      // step is clickable.
      styleOverrides: {
        root: {
          borderRadius: 10,
          '&:hover': { backgroundColor: 'transparent' },
          '& .MuiTouchRipple-root': { color: 'rgba(16,138,230,0.2)' },
        },
      },
    },
    MuiMenuItem: {
      // Soft brand-tinted hover/selected on dropdown and menu items, not the
      // default muddy grey wash.
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(16,138,230,0.06)' },
          '&.Mui-selected': { backgroundColor: 'rgba(16,138,230,0.1)' },
          '&.Mui-selected:hover': { backgroundColor: 'rgba(16,138,230,0.14)' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { fontSize: 14, backgroundColor: '#fff', borderRadius: radius.input },
        // No notch: the label lives above the box now.
        notchedOutline: { top: 0, borderColor: ink.border, '& legend': { display: 'none' } },
      },
    },
    // A long table scrolls inside its container so the header row stays in view.
    MuiTableContainer: {
      // Sticky only here, where the table scrolls in its own box; a table without a
      // container would pin its header to the page top, away from its rows.
      styleOverrides: { root: {
        maxHeight: 'max(420px, calc(100vh - 240px))',
        '& .MuiTableCell-head': { position: 'sticky', top: 0, zIndex: 2, backgroundColor: ink.headBg },
      } },
    },
    MuiTableCell: {
      styleOverrides: {
        // .data-table thead th: grey band, small uppercase labels.
        head: {
          fontWeight: 700,
          background: ink.headBg,
          color: ink.headFg,
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '.3px',
          padding: '12px 10px',
          borderBottom: `1px solid ${ink.border}`,
          whiteSpace: 'nowrap',
          '&:first-of-type': { paddingLeft: 16 },
          '&:last-of-type': { paddingRight: 16 },
        },
        // .data-table tbody td
        body: {
          fontSize: 13.5,
          padding: '14px 10px',
          borderBottom: `1px solid ${ink.line}`,
          '&:first-of-type': { paddingLeft: 16 },
          '&:last-of-type': { paddingRight: 16 },
          // Gap between row actions, also when a disabled one sits in a Tooltip <span>.
          '& :is(.MuiIconButton-sizeSmall, span:has(> .MuiIconButton-sizeSmall)) + :is(.MuiIconButton-sizeSmall, span:has(> .MuiIconButton-sizeSmall))':
            { marginLeft: 6 },
          '& span:has(> .MuiIconButton-sizeSmall)': { display: 'inline-flex', verticalAlign: 'middle' },
          // A cell of row actions never wraps them onto a second line.
          '&:has(> .MuiIconButton-sizeSmall, > span > .MuiIconButton-sizeSmall)': { whiteSpace: 'nowrap' },
          '& .MuiIconButton-sizeSmall': ROW_ACTIONS,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: { borderRadius: radius.button, transition: 'background-color 140ms, color 140ms' },
      },
    },
    MuiFormControlLabel: {
      // MUI's default pulls the control 11px left of its container padding, so
      // every switch/radio/checkbox pokes out past the card's left edge. Zero
      // it so controls line up with the content next to them, everywhere.
      styleOverrides: {
        root: {
          marginLeft: 0,
          // Stacked switch rows otherwise touch; give each row air and the label a gap.
          '&:has(> .MuiSwitch-root)': { paddingBlock: 3 },
          '& > .MuiSwitch-root': { marginRight: 10 },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        // Modern compact toggle — slim rounded track, clean thumb, brand blue
        // when on. Deliberately identical at both sizes: `size="small"` (used
        // in table Status columns) otherwise shrinks the thumb to 16px, which
        // then sits off-centre in this track. Neutralise it for uniformity.
        root: {
          width: 40, height: 22, padding: 0, marginRight: 6, overflow: 'visible',
          '&.MuiSwitch-sizeSmall': { width: 40, height: 22 },
          '&.MuiSwitch-sizeSmall .MuiSwitch-thumb': { width: 18, height: 18 },
          '&.MuiSwitch-sizeSmall .MuiSwitch-switchBase': { padding: 0, margin: '2px' },
          '&.MuiSwitch-sizeSmall .MuiSwitch-switchBase.Mui-checked': { transform: 'translateX(18px)' },
        },
        switchBase: {
          padding: 0,
          margin: '2px',
          transitionDuration: '180ms',
          color: '#fff',
          '&.Mui-checked': {
            transform: 'translateX(18px)',
            color: '#fff',
            '& + .MuiSwitch-track': { backgroundColor: '#108ae6', opacity: 1, border: 0 },
          },
          // Disabled: muted but legible (reads as "locked", not broken/faded).
          // Full track opacity with softened colours + a dimmer thumb, instead
          // of washing the whole control to near-transparent.
          '&.Mui-disabled': {
            color: '#f2f5f8',
            '& + .MuiSwitch-track': { opacity: 1, backgroundColor: '#dce2e8' },
          },
          '&.Mui-disabled.Mui-checked + .MuiSwitch-track': { opacity: 1, backgroundColor: '#a6ccef' },
        },
        thumb: {
          boxSizing: 'border-box',
          width: 18, height: 18,
          boxShadow: '0 1px 3px rgba(24,40,72,0.28)',
          '.Mui-disabled &': { boxShadow: '0 1px 2px rgba(24,40,72,0.12)' },
        },
        track: {
          borderRadius: 11,
          backgroundColor: '#c7d0da',
          opacity: 1,
          transition: 'background-color 180ms',
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        root: { backgroundColor: '#fff', borderRadius: 8 },
      },
    },
    MuiToggleButton: {
      // Clean segmented control: brand-tinted when selected (not MUI's muddy
      // grey wash), plain otherwise. One definition for every segmented filter.
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          fontSize: 13.5,
          color: '#5c6773',
          borderColor: '#dbe3ea',
          padding: '5px 14px',
          '&:hover': { backgroundColor: '#f3f7fb' },
          '&.Mui-selected': {
            backgroundColor: '#e8f4fd',
            color: '#108ae6',
            borderColor: '#bcdff7',
            '&:hover': { backgroundColor: '#dceffc' },
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        // .data-table tbody tr:hover td
        root: { '&:hover > .MuiTableCell-body': { background: '#fafcff' } },
      },
    },
    MuiChip: {
      styleOverrides: {
        // Lato's cap-height sits high in the box, so the label reads slightly
        // above centre. Flex-centre the label and let line-height match the
        // chip so status pills (CALCULATED, APPLIED, …) sit dead centre.
        // Soft-tinted filled status pills instead of loud saturated fills, so a
        // PUBLISHED / Active / Paid chip reads as a calm status and every status
        // pill carries the same visual weight across the app. Applied via class
        // selectors (the filled<Color> styleOverrides keys aren't typed).
        root: {
          fontSize: 12, height: 22, fontWeight: 700, alignItems: 'center', borderRadius: radius.pill,
          '&.MuiChip-outlined': { backgroundColor: '#fff', borderColor: '#d5dbe1' },
          '&.MuiChip-outlinedSuccess': { color: '#178a55', borderColor: '#9fd9bb' },
          '&.MuiChip-outlinedWarning': { color: '#c78a14', borderColor: '#f0c27a' },
          '&.MuiChip-outlinedError':   { color: '#e0614f', borderColor: '#f2b4ac' },
          '&.MuiChip-outlinedInfo, &.MuiChip-outlinedPrimary': { color: '#2a64c8', borderColor: '#9dbcf0' },
          '&.MuiChip-filledSuccess': { backgroundColor: '#e3f5ec', color: '#157a4b' },
          '&.MuiChip-filledError':   { backgroundColor: '#fbe7e3', color: '#b0433a' },
          '&.MuiChip-filledWarning': { backgroundColor: '#fdf3e0', color: '#8a5a10' },
          '&.MuiChip-filledInfo':    { backgroundColor: '#e7f3fd', color: '#1368a8' },
        },
        sizeSmall: { fontSize: 12, height: 22 },
        label: { lineHeight: 1, display: 'flex', alignItems: 'center', paddingTop: '1px' },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: { borderRadius: radius.notice, fontSize: 13.5 },
      },
    },
    // DataGrids don't inherit the MuiTableCell action styling above, so mirror
    // it here for the `actions` column — every grid's row actions match the
    // plain-table ones: semantic tinted boxes (delete red, disable amber,
    // activate green; view/edit neutral → blue on hover). NOTE: MUI X does not
    // always consume this theme override reliably, so <DataTable> also applies
    // the same block via its own sx (ACTION_ICON_SX) — keep the two in sync.
    MuiDataGrid: {
      styleOverrides: {
        root: {
          borderRadius: radius.table,
          '& .MuiDataGrid-cell[data-field="actions"] .MuiIconButton-root': ROW_ACTIONS,
        },
      },
    },
  },
};

export const theme = createTheme(themeOptions);
